import React, { useState, useEffect, useCallback, useRef } from "react";
import Footer from "../../../home_page/Footer";
import { useDispatch } from "react-redux";
import { closeModal } from "../../../../redux/actions/action-creator";

/* ─── Option sets ──────────────────────────────────────────────── */
const YES_NO = ["Yes", "No"];

const FORMATE_OF_REPORT_OPTIONS    = ["Condensed", "Detailed"];
const EXPAND_LEVEL_OPTIONS         = ["Yes", "No"];
const DISPLAY_NAME_OPTIONS         = ["Name Only", "Alias and Name", "Name and Alias"];
const SORTING_METHOD_OPTIONS       = ["Default", "Alphabetical", "Highest Value", "Lowest Value", "Highest Qty", "Lowest Qty"];
const TYPE_OF_GROUPING_OPTIONS     = ["Stock Item", "Stock Group", "Stock Category", "Godown"];
const GODOWNS_FORMATE_OPTIONS      = ["Hide", "Show"];

/* ─── Field config: [key, label, options[]] ────────────────────── */
const FIELDS = [
  { key: "show_quantity",                        label: "Show Quantity",                           opts: YES_NO },
  { key: "show_alternate_unit",                  label: "Show Alternate Unit",                     opts: YES_NO },
  { key: "show_tail_unit_of_compound_unit",      label: "Show Tail Unit of Compound Unit",         opts: YES_NO },
  { key: "show_rate",                            label: "Show Rate",                               opts: YES_NO },
  { key: "show_value",                           label: "Show Value",                              opts: YES_NO },
  { key: "show_opening_balance",                 label: "Show Opening Balance",                    opts: YES_NO },
  { key: "show_goods_inwards",                   label: "Show Goods Inwards",                      opts: YES_NO },
  { key: "show_goods_outwards",                  label: "Show Goods Outwards",                     opts: YES_NO },
  { key: "show_gross_value_of_outwards",         label: "Show Gross Value of Outwards",            opts: YES_NO },
  { key: "show_comsumption_and_gross_profit",    label: "Show Consumption and Gross Profit",       opts: YES_NO },
  { key: "show_gross_profits_on_gross_value",    label: "Show Gross Profits on Gross Value",       opts: YES_NO },
  { key: "show_closing_balance",                 label: "Show Closing Balance",                    opts: YES_NO },
  { key: "show_stock_item_with_zero_qty_or_blc", label: "Show Stock Item With Zero Qty or Bal",   opts: YES_NO },
  { key: "exclude_detail_with_no_transaction",   label: "Exclude Detail With No Transaction",      opts: YES_NO },
  { key: "type_of_grouping",                     label: "Type of Grouping",                        opts: TYPE_OF_GROUPING_OPTIONS },
  { key: "formate_of_report",                    label: "Format of Report",                        opts: FORMATE_OF_REPORT_OPTIONS },
  { key: "expand_all_level_in_details_formate",  label: "Expand All Level in Details Format",      opts: EXPAND_LEVEL_OPTIONS },
  { key: "display_name",                         label: "Display Name",                            opts: DISPLAY_NAME_OPTIONS },
  { key: "sorting_method",                       label: "Sorting Method",                          opts: SORTING_METHOD_OPTIONS },
];

/* ─── Single field row ─────────────────────────────────────────── */
function FieldRow({ field, value, onChange, onEnter, isFocused, inputRef }) {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const wrapRef = useRef(null);

  // Sync highlighted index with current value
  useEffect(() => {
    const idx = field.opts.findIndex(
      (o) => o.toLowerCase() === (value || "").toLowerCase()
    );
    setHighlighted(idx >= 0 ? idx : 0);
  }, [value, field.opts]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
      } else {
        setHighlighted((prev) => Math.min(prev + 1, field.opts.length - 1));
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (open) setHighlighted((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open) {
        // Select highlighted item and move to next field
        onChange(field.key, field.opts[highlighted]);
        setOpen(false);
        onEnter();
      } else {
        // Open dropdown on Enter
        setOpen(true);
      }
    } else if (e.key === "Escape") {
      if (open) {
        setOpen(false);
      }
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    onChange(field.key, val);
    setOpen(true);
    // Try to highlight matching option
    const idx = field.opts.findIndex((o) =>
      o.toLowerCase().startsWith(val.toLowerCase())
    );
    if (idx >= 0) setHighlighted(idx);
  };

  const filteredOpts = field.opts.filter((o) =>
    o.toLowerCase().includes((value || "").toLowerCase())
  );

  return (
    <div
      className="d-flex justify-content-between align-items-center"
      style={{ marginBottom: "1px", position: "relative" }}
      ref={wrapRef}
    >
      <label
        className="lab-1"
        style={{
          color: isFocused ? "#0d6efd" : "inherit",
          fontWeight: isFocused ? "bold" : "normal",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
      >
        {field.label}
      </label>
      <label style={{ margin: "0 4px" }}>:</label>
      <div style={{ position: "relative", minWidth: "110px" }}>
        <input
          ref={inputRef}
          type="text"
          name={field.key}
          id={field.key}
          value={value || ""}
          autoComplete="off"
          className="lab-right"
          style={{
            border: isFocused ? "1px solid #0d6efd" : undefined,
            background: isFocused ? "#eef4ff" : undefined,
          }}
          onChange={handleInputChange}
          onFocus={() => {
            setOpen(true);
            setTimeout(() => {
              if (inputRef?.current) {
                inputRef.current.setSelectionRange(0, inputRef.current.value.length);
              }
            }, 30);
          }}
          onBlur={() => {
            setTimeout(() => setOpen(false), 150);
            // Auto-correct to closest match on blur
            const exact = field.opts.find(
              (o) => o.toLowerCase() === (value || "").toLowerCase()
            );
            if (!exact && field.opts.length > 0) {
              onChange(field.key, field.opts[0]);
            }
          }}
          onKeyDown={handleKeyDown}
        />
        {open && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              right: 0,
              left: 0,
              zIndex: 9999,
              background: "#fff",
              border: "1px solid #0d6efd",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              borderRadius: "2px",
              maxHeight: "180px",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                background: "skyblue",
                padding: "2px 6px",
                fontSize: "11px",
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              {field.label}
            </div>
            {(filteredOpts.length > 0 ? filteredOpts : field.opts).map((opt, i) => (
              <div
                key={opt}
                onMouseDown={() => {
                  onChange(field.key, opt);
                  setOpen(false);
                  onEnter();
                }}
                style={{
                  padding: "3px 8px",
                  cursor: "pointer",
                  fontSize: "12px",
                  background:
                    field.opts.indexOf(opt) === highlighted
                      ? "#0d6efd"
                      : "transparent",
                  color: field.opts.indexOf(opt) === highlighted ? "#fff" : "#000",
                }}
                onMouseEnter={() => setHighlighted(field.opts.indexOf(opt))}
              >
                {opt}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Main Configure component ─────────────────────────────────── */
function Configure(props) {
  const dispatch = useDispatch();

  // Build initial state from props, mapping items_formate_of_report → formate_of_report
  const [state, setState] = useState(() => {
    const s = props.state_configure || {};
    return {
      show_quantity:                        s.show_quantity                        || "No",
      show_alternate_unit:                  s.show_alternate_unit                  || "No",
      show_tail_unit_of_compound_unit:      s.show_tail_unit_of_compound_unit      || "No",
      show_rate:                            s.show_rate                            || "No",
      show_value:                           s.show_value                           || "No",
      show_opening_balance:                 s.show_opening_balance                 || "No",
      show_goods_inwards:                   s.show_goods_inwards                   || "No",
      show_goods_outwards:                  s.show_goods_outwards                  || "No",
      show_gross_value_of_outwards:         s.show_gross_value_of_outwards         || "No",
      show_comsumption_and_gross_profit:    s.show_comsumption_and_gross_profit    || "No",
      show_gross_profits_on_gross_value:    s.show_gross_profits_on_gross_value    || "No",
      show_closing_balance:                 s.show_closing_balance                 || "No",
      show_stock_item_with_zero_qty_or_blc: s.show_stock_item_with_zero_qty_or_blc|| "No",
      exclude_detail_with_no_transaction:   s.exclude_detail_with_no_transaction   || "No",
      type_of_grouping:                     s.type_of_grouping                     || "",
      formate_of_report:                    s.items_formate_of_report || s.formate_of_report || "Condensed",
      expand_all_level_in_details_formate:  s.expand_all_level_in_details_formate  || "No",
      display_name:                         s.display_name                         || "Name Only",
      sorting_method:                       s.sorting_method                       || "Default",
    };
  });

  const [focusedIndex, setFocusedIndex] = useState(0);

  // One ref per field
  const refs = useRef(FIELDS.map(() => React.createRef()));

  // Auto-focus first field on open
  useEffect(() => {
    setTimeout(() => refs.current[0]?.current?.focus(), 80);
  }, []);

  // Escape closes modal
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        dispatch(closeModal("configure"));
      }
    };
    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, [dispatch]);

  useEffect(() => {
    if (props.setLoading) props.setLoading(false);
  }, []);

  const handleChange = useCallback((key, val) => {
    setState((prev) => ({ ...prev, [key]: val }));
  }, []);

  // Move focus to next field; on last field → apply & close
  const handleEnterNext = useCallback(
    (index) => {
      if (index < FIELDS.length - 1) {
        setFocusedIndex(index + 1);
        setTimeout(() => refs.current[index + 1]?.current?.focus(), 30);
      } else {
        // Last field → apply configure
        applyAndClose();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  const applyAndClose = useCallback(() => {
    props.setState_configure({
      show_quantity:                        state.show_quantity,
      show_alternate_unit:                  state.show_alternate_unit,
      show_tail_unit_of_compound_unit:      state.show_tail_unit_of_compound_unit,
      show_rate:                            state.show_rate,
      show_value:                           state.show_value,
      show_opening_balance:                 state.show_opening_balance,
      show_goods_inwards:                   state.show_goods_inwards,
      show_goods_outwards:                  state.show_goods_outwards,
      show_gross_value_of_outwards:         state.show_gross_value_of_outwards,
      show_comsumption_and_gross_profit:    state.show_comsumption_and_gross_profit,
      show_gross_profits_on_gross_value:    state.show_gross_profits_on_gross_value,
      show_closing_balance:                 state.show_closing_balance,
      show_stock_item_with_zero_qty_or_blc: state.show_stock_item_with_zero_qty_or_blc,
      exclude_detail_with_no_transaction:   state.exclude_detail_with_no_transaction,
      type_of_grouping:                     state.type_of_grouping,
      items_formate_of_report:              state.formate_of_report,
      expand_all_level_in_details_formate:  state.expand_all_level_in_details_formate,
      display_name:                         state.display_name,
      sorting_method:                       state.sorting_method,
    });
    dispatch(closeModal("configure"));
  }, [state, props, dispatch]);

  return (
    <div className="my-card">
      <div
        className="cross_button"
        onClick={() => dispatch(closeModal("configure"))}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          className="bi bi-x"
          viewBox="0 0 16 16"
        >
          <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
        </svg>
      </div>

      <div
        className="container pb-3 border border-dark"
        id="card-period"
        style={{ width: "60%", background: "#fff" }}
      >
        <div className="row">
          <div className="col border-bottom">
            <b>Configure</b>
          </div>
        </div>

        <div className="row">
          <div className="col mt-1">
            {FIELDS.map((field, idx) => (
              <FieldRow
                key={field.key}
                field={field}
                value={state[field.key]}
                onChange={handleChange}
                onEnter={() => handleEnterNext(idx)}
                isFocused={focusedIndex === idx}
                inputRef={refs.current[idx]}
              />
            ))}
          </div>
        </div>

        {/* Apply button */}
        <div className="row mt-2">
          <div className="col text-end">
            <button
              className="btn btn-sm btn-primary px-3"
              style={{ fontSize: "12px" }}
              onClick={applyAndClose}
            >
              Apply (Enter)
            </button>
          </div>
        </div>
      </div>

      <div className="tomasterfooter" style={{ background: "red" }}>
        <Footer />
      </div>
    </div>
  );
}

export default Configure;